import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Edit2, Trophy, CheckCircle, Circle, Star, Zap, 
  Target, Calendar, RotateCcw, Award, Crown, Sparkles, Home, Coffee, 
  Utensils, Bath, Sofa, Car, ShoppingCart, Dog, Briefcase, Heart,
  Folder, X, LogOut, History, ChevronLeft, ChevronRight
} from 'lucide-react';
import * as authService from './firebaseAuthService';
import * as firestoreService from './firestoreService';
import { LoginScreen, SignupScreen } from './AuthScreens';
import { MyListsScreen } from './MyListsScreen';
import { ShareListModal } from './ShareListModal';
import { ToastContainer } from './Toast';
import './App.css';

// Available icons for categories
const CATEGORY_ICONS = {
  Home, Coffee, Utensils, Bath, Sofa, Car, ShoppingCart, 
  Dog, Briefcase, Heart, Target, Star, Sparkles
};

const ICON_NAMES = Object.keys(CATEGORY_ICONS);

// Confetti animation component
const Confetti = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="confetti-container">
      {[...Array(30)].map((_, i) => (
        <div
          key={i}
          className="confetti"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 0.5}s`,
            backgroundColor: ['#FFD700', '#FF69B4', '#00CED1', '#FF6347', '#9370DB'][Math.floor(Math.random() * 5)]
          }}
        />
      ))}
    </div>
  );
};

// Points popup animation
const PointsPopup = ({ points, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 1500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="points-popup">
      +{points} <Star size={20} />
    </div>
  );
};

export default function ChoreTracker() {
  // Authentication state
  const [authUser, setAuthUser] = useState(null);
  const [showLogin, setShowLogin] = useState(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Lists state (NEW in Step 2)
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [showMyLists, setShowMyLists] = useState(true); // Show "My Lists" screen by default
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingList, setSharingList] = useState(null);
  const [toasts, setToasts] = useState([]);

  // Original app state (now scoped to selected list)
  const [currentUser, setCurrentUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [chores, setChores] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [activeView, setActiveView] = useState('chores');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showAddChore, setShowAddChore] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedChoreForHistory, setSelectedChoreForHistory] = useState(null);
  const [editingChore, setEditingChore] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showPointsPopup, setShowPointsPopup] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(null);
  
  // Form states
  const [choreForm, setChoreForm] = useState({
    name: '',
    type: 'daily',
    categoryId: null,
    points: 10,
    dailyLimit: 1
  });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: 'Home',
    color: '#667eea'
  });

  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // User color options
  const colorOptions = ['#FF69B4', '#FFD700', '#00CED1', '#FF6347', '#9370DB', '#32CD32', '#FF8C00', '#1E90FF'];

  // Toast notification helpers
  const showToast = (message, type = 'success', duration = 3000) => {
    const id = Date.now();
    
    // Prevent duplicate toasts with same message
    const isDuplicate = toasts.some(t => t.message === message && Date.now() - t.id < 1000);
    if (isDuplicate) return;
    
    setToasts(prev => [...prev, { id, message, type, duration }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Check if user is logged in on mount
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      setAuthUser(user);
      // Also set currentUser to avoid null errors
      setCurrentUser({
        id: user.uid,
        name: user.name,
        color: user.color
      });
    }
    setIsCheckingAuth(false);
  }, []);

  // Initialize selected month to current month
  useEffect(() => {
    const now = new Date();
    setSelectedMonth(`${now.getFullYear()}-${now.getMonth()}`);
  }, []);

  // Load data from Firestore with real-time listeners
useEffect(() => {
  if (!authUser) return;

  // Listen to user's lists
  const unsubscribeLists = firestoreService.getUserLists(authUser.uid, (listsData) => {
    setLists(listsData);
    
    // Restore selected list if it exists
    const savedSelectedListId = localStorage.getItem('selectedListId');
    if (savedSelectedListId) {
      const listToSelect = listsData.find(l => l.id === savedSelectedListId);
      if (listToSelect) {
        setSelectedList(listToSelect);
        setShowMyLists(false);
      }
    }
  });

  return () => {
    unsubscribeLists();
  };
}, [authUser]);

// Listen to selected list's data
useEffect(() => {
  if (!selectedList) {
    setChores([]);
    setCategories([]);
    setCompletions([]);
    return;
  }

  // Listen to chores
  const unsubscribeChores = firestoreService.getListChores(selectedList.id, (choresData) => {
    setChores(choresData);
  });

  // Listen to categories
  const unsubscribeCategories = firestoreService.getListCategories(selectedList.id, (categoriesData) => {
    setCategories(categoriesData);
  });

  // Listen to completions
  const unsubscribeCompletions = firestoreService.getListCompletions(selectedList.id, (completionsData) => {
    setCompletions(completionsData);
  });

  return () => {
    unsubscribeChores();
    unsubscribeCategories();
    unsubscribeCompletions();
  };
}, [selectedList]);

  // Save selected list ID to localStorage
  useEffect(() => {
    if (selectedList) {
      localStorage.setItem('selectedListId', selectedList.id);
    } else {
      localStorage.removeItem('selectedListId');
    }
  }, [selectedList]);

  // Check and reset chores based on their schedule
  useEffect(() => {
    const checkResets = () => {
      const now = new Date();
      const today = now.toDateString();
      const currentWeek = getWeekNumber(now);
      const currentMonth = `${now.getFullYear()}-${now.getMonth()}`;

      setCompletions(prev => {
        let updated = [...prev];
        let hasChanges = false;

        chores.forEach(chore => {
          const choreCompletions = updated.filter(c => c.choreId === chore.id);
          
          choreCompletions.forEach(completion => {
            const compDate = new Date(completion.date);
            const shouldReset = 
              (chore.type === 'daily' && compDate.toDateString() !== today) ||
              (chore.type === 'weekly' && getWeekNumber(compDate) !== currentWeek) ||
              (chore.type === 'monthly' && `${compDate.getFullYear()}-${compDate.getMonth()}` !== currentMonth);

            if (shouldReset) {
              updated = updated.filter(c => c.id !== completion.id);
              hasChanges = true;
            }
          });
        });

        return hasChanges ? updated : prev;
      });
    };

    const interval = setInterval(checkResets, 60000);
    checkResets();
    return () => clearInterval(interval);
  }, [chores]);

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Authentication handlers
  const handleLogin = async (email, password) => {
    const user = await authService.login(email, password);
    setAuthUser(user);
    // Set currentUser to the logged-in user (removing old user selection system)
    setCurrentUser({
      id: user.uid,
      name: user.name,
      color: user.color
    });
  };

  const handleSignup = async (email, password, name) => {
  const user = await authService.signUp(email, password, name);
  setAuthUser(user);
  setCurrentUser({
    id: user.uid,
    name: user.name,
    color: user.color
  });

  // Create default "Personal" list in Firestore
  const defaultListData = {
    name: 'Personal',
    ownerId: user.uid,
    members: [user.uid],
    memberDetails: {
      [user.uid]: {
        name: user.name,
        color: user.color
      }
    }
  };

  const createdList = await firestoreService.createList(defaultListData);

  // Create default categories in Firestore
  await firestoreService.createCategory({
    listId: createdList.id,
    name: 'Kitchen',
    icon: 'Utensils',
    color: '#FF6347'
  });

  await firestoreService.createCategory({
    listId: createdList.id,
    name: 'Bathroom',
    icon: 'Bath',
    color: '#00CED1'
  });

  await firestoreService.createCategory({
    listId: createdList.id,
    name: 'Living Room',
    icon: 'Sofa',
    color: '#9370DB'
  });

  // Lists will auto-load via useEffect listener
  };

  const handleLogout = async () => {
    await authService.logout();
    setAuthUser(null);
    setCurrentUser(null);
    setSelectedList(null);
    setShowMyLists(true);
    setShowLogin(true);
    localStorage.removeItem('selectedListId'); // Clear saved list on logout
  };

  const createNewList = async (listName) => {
  if (!listName || !listName.trim()) {
    alert('Please enter a list name');
    return;
  }

  const newListData = {
    name: listName.trim(),
    ownerId: authUser.uid,
    members: [authUser.uid],
    memberDetails: {
      [authUser.uid]: {
        name: currentUser.name,
        color: currentUser.color
      }
    }
  };

  await firestoreService.createList(newListData);
  // List will auto-appear via useEffect listener
  };

  const editListName = async (listId, newName) => {
  if (!newName || !newName.trim()) {
    alert('Please enter a list name');
    return;
  }

  await firestoreService.updateList(listId, { name: newName.trim() });
  // Will auto-update via useEffect listener
  };

  const updateListName = (listId, newName) => {
    if (!newName || !newName.trim()) {
      alert('Please enter a list name');
      return;
    }

    setLists(lists.map(list => 
      list.id === listId ? { ...list, name: newName.trim() } : list
    ));
  };

  const deleteList = async (listId) => {
  const list = lists.find(l => l.id === listId);
  if (!list) return;

  if (list.ownerId !== authUser.uid) {
    alert('Only the list owner can delete this list');
    return;
  }

  if (!window.confirm(`Are you sure you want to delete "${list.name}"?`)) {
    return;
  }

  await firestoreService.deleteListData(listId);

  if (selectedList?.id === listId) {
    setSelectedList(null);
    setShowMyLists(true);
  }
};

  const selectList = (list) => {
    setSelectedList(list);
    setShowMyLists(false);
  };

  const goBackToMyLists = () => {
    setSelectedList(null);
    setShowMyLists(true);
  };

  const getUserLists = () => {
    // Return all lists where user is owner or member
    return lists.filter(list => 
      list.ownerId === authUser?.uid || list.members.includes(authUser?.uid)
    );
  };

  // Sharing and member management functions
  const openShareModal = (list) => {
    setSharingList(list);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setSharingList(null);
    setShowShareModal(false);
  };

  const inviteMember = async (listId, email) => {
  try {
    const allUsers = await authService.getAllUsers();
    const userToInvite = allUsers.find(u => u.email === email);

    if (!userToInvite) {
      throw new Error('No user found with this email. They need to sign up first!');
    }

    if (userToInvite.uid === authUser.uid) {
      throw new Error('You cannot invite yourself!');
    }

    const list = lists.find(l => l.id === listId);
    if (!list) {
      throw new Error('List not found');
    }

    if (list.members.includes(userToInvite.uid)) {
      throw new Error(`${userToInvite.name} is already a member of this list`);
    }

    // Update in Firestore (THIS WAS MISSING!)
    const updatedMembers = [...list.members, userToInvite.uid];
    const updatedMemberDetails = {
      ...list.memberDetails,
      [userToInvite.uid]: {
        name: userToInvite.name,
        color: userToInvite.color
      }
    };

    await firestoreService.updateList(listId, {
      members: updatedMembers,
      memberDetails: updatedMemberDetails
    });

    showToast(`${userToInvite.name} added to list!`, 'success');
    
    // Refresh sharing modal
    const updatedList = lists.find(l => l.id === listId);
    setSharingList(updatedList);
  } catch (error) {
    throw error;
  }
  };

  const removeMember = async (listId, memberId) => {
  const list = lists.find(l => l.id === listId);
  if (!list || list.ownerId !== authUser.uid) {
    alert('Only the owner can remove members');
    return;
  }

  if (memberId === authUser.uid) {
    alert('You cannot remove yourself as the owner');
    return;
  }

  if (!window.confirm('Remove this member from the list?')) {
    return;
  }

  const updatedMembers = list.members.filter(id => id !== memberId);
  const updatedMemberDetails = { ...list.memberDetails };
  delete updatedMemberDetails[memberId];

  await firestoreService.updateList(listId, {
    members: updatedMembers,
    memberDetails: updatedMemberDetails
  });

  showToast('Member removed from list', 'success');
  setSharingList(lists.find(l => l.id === listId)); // Refresh modal
};

const leaveList = async (listId) => {
  const list = lists.find(l => l.id === listId);
  if (!list) return;

  if (list.ownerId === authUser.uid) {
    alert('You cannot leave a list you own. Delete it instead.');
    return;
  }

  if (!window.confirm('Leave this list?')) {
    return;
  }

  const updatedMembers = list.members.filter(id => id !== authUser.uid);
  const updatedMemberDetails = { ...list.memberDetails };
  delete updatedMemberDetails[authUser.uid];

  await firestoreService.updateList(listId, {
    members: updatedMembers,
    memberDetails: updatedMemberDetails
  });

  if (selectedList?.id === listId) {
    setSelectedList(null);
    setShowMyLists(true);
  }

  closeShareModal();
  showToast('You left the list', 'success');
};

  // Category functions
  const addCategory = async () => {
  if (categoryForm.name.trim()) {
    await firestoreService.createCategory({
      listId: selectedList.id,
      name: categoryForm.name.trim(),
      icon: categoryForm.icon,
      color: categoryForm.color
    });

    setCategoryForm({ name: '', icon: ICON_NAMES[0], color: '#FF6347' });
    setShowCategoryModal(false);
  }
};

const updateCategory = async () => {
  if (editingCategory && categoryForm.name.trim()) {
    await firestoreService.updateCategory(editingCategory.id, {
      name: categoryForm.name.trim(),
      icon: categoryForm.icon,
      color: categoryForm.color
    });

    setEditingCategory(null);
    setCategoryForm({ name: '', icon: ICON_NAMES[0], color: '#FF6347' });
    setShowCategoryModal(false);
  }
};

const deleteCategory = async (categoryId) => {
  if (window.confirm('Delete this category? Chores will remain but be uncategorized.')) {
    await firestoreService.deleteCategory(categoryId);
  }
};

  const startEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      icon: category.icon,
      color: category.color
    });
  };

  // Chore functions
  const openAddChore = () => {
    // Auto-set category if one is selected
    if (selectedCategory !== 'all') {
      setChoreForm({ ...choreForm, categoryId: selectedCategory });
    }
    setShowAddChore(true);
  };

  const addChore = async () => {
  if (!choreForm.categoryId) {
    alert('Please select or create a category first!');
    return;
  }

  if (choreForm.name.trim()) {
    await firestoreService.createChore({
      listId: selectedList.id,
      name: choreForm.name.trim(),
      type: choreForm.type,
      categoryId: choreForm.categoryId,
      points: choreForm.points,
      dailyLimit: choreForm.type === 'daily' ? choreForm.dailyLimit : 1
    });

    setChoreForm({ name: '', type: 'daily', categoryId: selectedCategory !== 'all' ? selectedCategory : null, points: 10, dailyLimit: 1 });
    setShowAddChore(false);
  }
};

const updateChore = async () => {
  if (editingChore && choreForm.name.trim()) {
    await firestoreService.updateChore(editingChore.id, {
      name: choreForm.name.trim(),
      type: choreForm.type,
      categoryId: choreForm.categoryId,
      points: choreForm.points,
      dailyLimit: choreForm.type === 'daily' ? choreForm.dailyLimit : 1
    });

    setEditingChore(null);
    setChoreForm({ name: '', type: 'daily', categoryId: null, points: 10, dailyLimit: 1 });
    setShowAddChore(false);
  }
};

const deleteChore = async (choreId) => {
  if (window.confirm('Are you sure you want to delete this chore?')) {
    await firestoreService.deleteChore(choreId);
  }
};

  const startEditChore = (chore) => {
    setEditingChore(chore);
    setChoreForm({
      name: chore.name,
      type: chore.type,
      categoryId: chore.categoryId,
      points: chore.points || 10,
      dailyLimit: chore.dailyLimit || 1
    });
    setShowAddChore(true);
  };

  const cancelEdit = () => {
    setEditingChore(null);
    setChoreForm({ name: '', type: 'daily', categoryId: selectedCategory !== 'all' ? selectedCategory : null, points: 10, dailyLimit: 1 });
    setShowAddChore(false);
  };

  const completeChore = async (choreId) => {
  if (!currentUser || !authUser) {
    alert('Please log in first!');
    return;
  }

  const chore = chores.find(c => c.id === choreId);
  if (!chore) return;

  if (isChoreCompleted(choreId)) {
    alert('This chore has already been completed for this period!');
    return;
  }

  if (chore.type === 'daily') {
    const todayCompletions = getChoreCompletionsToday(choreId);
    if (todayCompletions.length >= chore.dailyLimit) {
      alert(`This chore can only be completed ${chore.dailyLimit} time(s) per day!`);
      return;
    }
  }

  // Create completion in Firestore
  await firestoreService.createCompletion({
    listId: selectedList.id,
    choreId,
    userId: authUser.uid,
    userName: currentUser.name,
    userColor: currentUser.color,
    points: chore.points || 10
  });

  setShowConfetti(true);
  };

  const getChoreCompletionsToday = (choreId) => {
    const today = new Date().toDateString();
    return completions.filter(c => {
      if (c.choreId !== choreId) return false;
      const compDate = new Date(c.date);
      return compDate.toDateString() === today;
    });
  };

  const isChoreCompleted = (choreId) => {
    const chore = chores.find(c => c.id === choreId);
    if (!chore) return false;

    const now = new Date();
    const today = now.toDateString();
    const currentWeek = getWeekNumber(now);
    const currentMonth = `${now.getFullYear()}-${now.getMonth()}`;

    // For daily tasks with limit > 1, check if we've hit the limit
    if (chore.type === 'daily' && chore.dailyLimit > 1) {
      const todayCompletions = getChoreCompletionsToday(choreId);
      return todayCompletions.length >= chore.dailyLimit;
    }

    // For other tasks, check if ANY user has completed it in the current period
    return completions.some(c => {
      if (c.choreId !== choreId) return false;
      
      const compDate = new Date(c.date);
      return (
        (chore.type === 'daily' && compDate.toDateString() === today) ||
        (chore.type === 'weekly' && getWeekNumber(compDate) === currentWeek) ||
        (chore.type === 'monthly' && `${compDate.getFullYear()}-${compDate.getMonth()}` === currentMonth)
      );
    });
  };

  const getChoreCompletionCount = (choreId) => {
    const chore = chores.find(c => c.id === choreId);
    if (!chore) return 0;

    if (chore.type === 'daily' && chore.dailyLimit > 1) {
      return getChoreCompletionsToday(choreId).length;
    }

    return isChoreCompleted(choreId) ? 1 : 0;
  };

  // Sequential task feature removed

  const getUserStats = (userId, monthKey = null) => {
    // If monthKey is provided, use it; otherwise use current month
    const now = new Date();
    const targetMonth = monthKey || `${now.getFullYear()}-${now.getMonth()}`;
    
    const monthlyCompletions = completions.filter(c => {
      const compDate = new Date(c.date);
      return c.userId === userId && 
             c.listId === selectedList?.id && // ✅ Filter by current list
             `${compDate.getFullYear()}-${compDate.getMonth()}` === targetMonth;
    });

    const totalPoints = monthlyCompletions.reduce((sum, c) => sum + (c.points || 10), 0);
    
    return {
      count: monthlyCompletions.length,
      points: totalPoints
    };
  };

  const getLeaderboard = (monthKey = null) => {
    if (!selectedList || !authUser) return [];
    
    // Get all members of the current list
    const members = selectedList.members || [];
    
    // Build leaderboard with stats for each member
    const leaderboard = members.map(memberId => {
      const memberInfo = selectedList.memberDetails[memberId];
      return {
        id: memberId,
        name: memberInfo.name,
        color: memberInfo.color,
        stats: getUserStats(memberId, monthKey),
        isCurrentUser: memberId === authUser.uid // Use authUser.uid for consistency
      };
    });

    // Sort by points (highest first)
    return leaderboard.sort((a, b) => b.stats.points - a.stats.points);
  };

  const getAvailableMonths = () => {
    const monthSet = new Set();
    
    // Add all months that have completions for this list
    completions
      .filter(c => c.listId === selectedList?.id)
      .forEach(c => {
        const date = new Date(c.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        monthSet.add(monthKey);
      });
    
    // Always include current month
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${now.getMonth()}`;
    monthSet.add(currentMonthKey);
    
    // If we have at least 2 months, fill in gaps between earliest and latest
    if (monthSet.size >= 2) {
      const monthArray = Array.from(monthSet).sort();
      const earliestMonthKey = monthArray[0];
      const latestMonthKey = monthArray[monthArray.length - 1];
      
      const [earliestYear, earliestMonth] = earliestMonthKey.split('-').map(Number);
      const [latestYear, latestMonth] = latestMonthKey.split('-').map(Number);
      
      let year = earliestYear;
      let month = earliestMonth;
      
      // Fill in all months between earliest and latest
      while (year < latestYear || (year === latestYear && month <= latestMonth)) {
        monthSet.add(`${year}-${month}`);
        month++;
        if (month > 11) {
          month = 0;
          year++;
        }
      }
    }
    
    return Array.from(monthSet).sort().reverse();
  };

  const getMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month), 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const changeMonth = (direction) => {
    const availableMonths = getAvailableMonths();
    if (availableMonths.length === 0) return;
    
    const currentIndex = availableMonths.indexOf(selectedMonth);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex + 1;
    } else {
      newIndex = currentIndex - 1;
    }
    
    if (newIndex >= 0 && newIndex < availableMonths.length) {
      setSelectedMonth(availableMonths[newIndex]);
    }
  };

  const getFilteredChores = () => {
    // Filter by selected list first
    let listChores = chores.filter(c => c.listId === selectedList?.id);
    
    // Then filter by category if selected
    if (selectedCategory === 'all') {
      return listChores;
    }
    return listChores.filter(c => c.categoryId === selectedCategory);
  };

  const getChoreHistory = (choreId) => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${now.getMonth()}`;
    
    // Get all completions for this chore in the current month
    const monthlyCompletions = completions.filter(c => {
      if (c.choreId !== choreId) return false;
      const compDate = new Date(c.date);
      return `${compDate.getFullYear()}-${compDate.getMonth()}` === currentMonth;
    });

    // Sort by date (newest first)
    // User info is already stored in completion
    return monthlyCompletions.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const openHistory = (chore) => {
    setSelectedChoreForHistory(chore);
    setShowHistory(true);
  };

  const closeHistory = () => {
    setShowHistory(false);
    setSelectedChoreForHistory(null);
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="app login-screen">
        <div className="login-container">
          <Sparkles size={64} className="login-icon" />
          <h1>Chore Quest</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // If not logged in, show auth screens
  if (!authUser) {
    if (showLogin) {
      return (
        <LoginScreen
          onLogin={handleLogin}
          onSwitchToSignup={() => setShowLogin(false)}
        />
      );
    } else {
      return (
        <SignupScreen
          onSignup={handleSignup}
          onSwitchToLogin={() => setShowLogin(true)}
        />
      );
    }
  }

  // User is logged in - show main app
  // If showing My Lists screen, render it
  if (showMyLists) {
    return (
      <>
        <MyListsScreen
          lists={getUserLists()}
          currentUser={currentUser}
          onSelectList={selectList}
          onCreateList={createNewList}
          onUpdateList={updateListName}
          onDeleteList={deleteList}
          onShare={openShareModal}
          onLogout={handleLogout}
        />
        
        {showShareModal && sharingList && (
          <ShareListModal
            list={sharingList}
            currentUser={currentUser}
            onClose={closeShareModal}
            onInviteMember={inviteMember}
            onRemoveMember={removeMember}
            onLeaveList={leaveList}
          />
        )}
      </>
    );
  }

  // If no list selected, show My Lists
  if (!selectedList) {
    setShowMyLists(true);
    return null;
  }

  // User has selected a list - show chores/leaderboard for that list
  return (
    <div className="app">
      {showConfetti && (
        <Confetti onComplete={() => setShowConfetti(false)} />
      )}
      
      {showPointsPopup && (
        <PointsPopup points={earnedPoints} onComplete={() => setShowPointsPopup(false)} />
      )}

      <header className="app-header">
        <button className="btn-back" onClick={goBackToMyLists} title="Back to My Lists">
          <ChevronLeft size={24} />
        </button>
        <h1><Sparkles size={24} /> {selectedList.name}</h1>
        <button className="btn-logout" onClick={handleLogout}>
          <LogOut size={20} />
        </button>
      </header>

      <nav className="app-nav">
        <button 
          className={activeView === 'chores' ? 'active' : ''}
          onClick={() => setActiveView('chores')}
        >
          <Target size={20} />
          <span>Chores</span>
        </button>
        <button 
          className={activeView === 'leaderboard' ? 'active' : ''}
          onClick={() => setActiveView('leaderboard')}
        >
          <Trophy size={20} />
          <span>Leaderboard</span>
        </button>
      </nav>

      <main className="app-main">
        {activeView === 'chores' && (
          <div className="view-container">
            <div className="view-header">
              <h2><Target size={24} /> My Chores</h2>
              <div className="header-actions">
                <button className="btn-secondary btn-small" onClick={() => setShowCategoryManager(true)}>
                  <Folder size={18} /> Categories
                </button>
                <button className="btn-primary" onClick={openAddChore}>
                  <Plus size={20} /> Add
                </button>
              </div>
            </div>

            {/* Category filter */}
            <div className="category-filter">
              <button
                className={`category-filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                All
              </button>
              {categories.filter(cat => cat.listId === selectedList.id).map(cat => {
                const Icon = CATEGORY_ICONS[cat.icon] || Home;
                return (
                  <button
                    key={cat.id}
                    className={`category-filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat.id)}
                    style={{ 
                      borderColor: selectedCategory === cat.id ? cat.color : 'transparent',
                      color: selectedCategory === cat.id ? cat.color : '#6c757d'
                    }}
                  >
                    <Icon size={16} />
                    {cat.name}
                  </button>
                );
              })}
            </div>

            {getFilteredChores().length === 0 ? (
              <div className="empty-state">
                <Sparkles size={48} />
                <h3>No chores here!</h3>
                <p>{selectedCategory === 'all' ? 'Add your first chore to get started' : 'No chores in this category yet'}</p>
              </div>
            ) : (
              <div className="chores-list">
                {getFilteredChores().map(chore => {
                  const nextUser = null; // Sequential feature removed
                  const completionCount = getChoreCompletionCount(chore.id);
                  const isCompleted = isChoreCompleted(chore.id);
                  const category = categories.find(c => c.id === chore.categoryId);
                  const CategoryIcon = category ? CATEGORY_ICONS[category.icon] : null;
                  const canComplete = !isCompleted; // Simplified - no sequential check
                  
                  return (
                    <div key={chore.id} className="chore-card">
                      <div className="chore-header">
                        <div className="chore-title-section">
                          <div className="chore-title-row">
                            {CategoryIcon && (
                              <div className="chore-category-icon" style={{ color: category.color }}>
                                <CategoryIcon size={20} />
                              </div>
                            )}
                            <h3>{chore.name}</h3>
                          </div>
                          <div className="chore-meta">
                            <span className={`badge badge-${chore.type}`}>
                              {chore.type === 'daily' && <Calendar size={12} />}
                              {chore.type === 'weekly' && <RotateCcw size={12} />}
                              {chore.type === 'monthly' && <Calendar size={12} />}
                              {chore.type}
                            </span>
                            {/* Sequential badge removed */}
                            <span className="points-badge">
                              <Star size={12} /> {chore.points} pts
                            </span>
                            {chore.type === 'daily' && chore.dailyLimit > 1 && (
                              <span className="badge badge-limit">
                                {completionCount}/{chore.dailyLimit} today
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="chore-actions">
                          <button className="btn-icon" onClick={() => openHistory(chore)} title="View History">
                            <History size={18} />
                          </button>
                          <button className="btn-icon" onClick={() => startEditChore(chore)}>
                            <Edit2 size={18} />
                          </button>
                          <button className="btn-icon-danger" onClick={() => deleteChore(chore.id)}>
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>

                      {/* Sequential "Next up" indicator removed */}

                      <button
                        className={`complete-chore-btn ${!canComplete ? 'disabled' : ''} ${isCompleted ? 'completed' : ''}`}
                        onClick={() => canComplete && completeChore(chore.id)}
                        disabled={!canComplete}
                        style={{
                          borderColor: currentUser.color,
                          backgroundColor: isCompleted ? currentUser.color : 'transparent',
                          color: isCompleted ? 'white' : currentUser.color
                        }}
                      >
                        {isCompleted ? (
                          <>
                            <CheckCircle size={20} />
                            <span>Completed!</span>
                          </>
                        ) : (
                          <>
                            <Circle size={20} />
                            <span>Mark as Done</span>
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Category Manager Modal - Continues in next part */}

            {/* Category Manager Modal */}
            {showCategoryManager && (
              <div className="modal-overlay" onClick={() => { setShowCategoryManager(false); setEditingCategory(null); setCategoryForm({ name: '', icon: 'Home', color: '#667eea' }); }}>
                <div className="modal large-modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3><Folder size={24} /> Manage Categories</h3>
                    <button className="btn-close" onClick={() => { setShowCategoryManager(false); setEditingCategory(null); setCategoryForm({ name: '', icon: 'Home', color: '#667eea' }); }}>
                      <X size={20} />
                    </button>
                  </div>

                  <div className="categories-list">
                    {categories.map(category => {
                      const Icon = CATEGORY_ICONS[category.icon] || Home;
                      const categoryChores = chores.filter(c => c.categoryId === category.id);
                      
                      return (
                        <div 
                          key={category.id} 
                          className="category-item"
                          style={{ borderLeftColor: category.color }}
                        >
                          <div className="category-icon-small" style={{ backgroundColor: category.color }}>
                            <Icon size={20} />
                          </div>
                          <div className="category-details">
                            <h4>{category.name}</h4>
                            <p>{categoryChores.length} chore{categoryChores.length !== 1 ? 's' : ''}</p>
                          </div>
                          <div className="category-actions">
                            <button className="btn-icon" onClick={() => startEditCategory(category)}>
                              <Edit2 size={16} />
                            </button>
                            <button className="btn-icon-danger" onClick={() => deleteCategory(category.id)}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add/Edit Category Form */}
                  <div className="category-form">
                    <h4>{editingCategory ? 'Edit Category' : 'Add New Category'}</h4>
                    
                    <div className="form-group">
                      <label>Category Name</label>
                      <input
                        type="text"
                        placeholder="e.g., Kitchen, Yard"
                        value={categoryForm.name}
                        onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label>Icon</label>
                      <div className="icon-picker">
                        {ICON_NAMES.map(iconName => {
                          const Icon = CATEGORY_ICONS[iconName];
                          return (
                            <button
                              key={iconName}
                              className={`icon-option ${categoryForm.icon === iconName ? 'selected' : ''}`}
                              onClick={() => setCategoryForm({ ...categoryForm, icon: iconName })}
                              type="button"
                            >
                              <Icon size={20} />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Color</label>
                      <div className="color-options">
                        {colorOptions.map(color => (
                          <div
                            key={color}
                            className={`color-option ${categoryForm.color === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setCategoryForm({ ...categoryForm, color })}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="form-actions">
                      {editingCategory && (
                        <button className="btn-secondary" onClick={() => {
                          setEditingCategory(null);
                          setCategoryForm({ name: '', icon: 'Home', color: '#667eea' });
                        }}>
                          Cancel Edit
                        </button>
                      )}
                      <button 
                        className="btn-primary" 
                        onClick={editingCategory ? updateCategory : addCategory}
                        disabled={!categoryForm.name.trim()}
                      >
                        {editingCategory ? 'Update' : 'Add'} Category
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Add/Edit Chore Modal */}
            {showAddChore && (
              <div className="modal-overlay" onClick={cancelEdit}>
                <div className="modal chore-modal" onClick={e => e.stopPropagation()}>
                  <h3>{editingChore ? 'Edit Chore' : 'Add New Chore'}</h3>
                  
                  <div className="form-group">
                    <label>Chore Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Wash the dishes"
                      value={choreForm.name}
                      onChange={e => setChoreForm({ ...choreForm, name: e.target.value })}
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={choreForm.categoryId || ''}
                      onChange={e => setChoreForm({ ...choreForm, categoryId: e.target.value ? parseInt(e.target.value) : null })}
                    >
                      <option value="">Select a category...</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    <small style={{ color: '#718096', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                      Need a new category? Click "Categories" button above
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Repeat</label>
                    <select
                      value={choreForm.type}
                      onChange={e => setChoreForm({ ...choreForm, type: e.target.value })}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Points</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={choreForm.points}
                        onChange={e => setChoreForm({ ...choreForm, points: parseInt(e.target.value) || 10 })}
                      />
                    </div>

                    {choreForm.type === 'daily' && (
                      <div className="form-group">
                        <label>Daily Limit</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={choreForm.dailyLimit}
                          onChange={e => setChoreForm({ ...choreForm, dailyLimit: parseInt(e.target.value) || 1 })}
                        />
                      </div>
                    )}
                  </div>

                  {/* Sequential checkbox removed */}

                  <div className="modal-actions">
                    <button className="btn-secondary" onClick={cancelEdit}>Cancel</button>
                    <button className="btn-primary" onClick={editingChore ? updateChore : addChore}>
                      {editingChore ? 'Update' : 'Add'} Chore
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* History Modal */}
            {showHistory && selectedChoreForHistory && (
              <div className="modal-overlay" onClick={closeHistory}>
                <div className="modal history-modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3><History size={24} /> Completion History</h3>
                    <button className="btn-close" onClick={closeHistory}>
                      <X size={20} />
                    </button>
                  </div>

                  <div className="history-chore-info">
                    <h4>{selectedChoreForHistory.name}</h4>
                    <p className="history-month">
                      {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </p>
                  </div>

                  {(() => {
                    const history = getChoreHistory(selectedChoreForHistory.id);
                    if (history.length === 0) {
                      return (
                        <div className="empty-state">
                          <History size={48} />
                          <h3>No completions yet</h3>
                          <p>This chore hasn't been completed this month</p>
                        </div>
                      );
                    }

                    return (
                      <div className="history-list">
                        {history.map((item, index) => {
                          const completionDate = new Date(item.date);
                          const isToday = completionDate.toDateString() === new Date().toDateString();
                          const isYesterday = new Date(completionDate).setHours(0,0,0,0) === 
                                             new Date(new Date().setDate(new Date().getDate() - 1)).setHours(0,0,0,0);
                          
                          let dateLabel;
                          if (isToday) {
                            dateLabel = `Today at ${completionDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                          } else if (isYesterday) {
                            dateLabel = `Yesterday at ${completionDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                          } else {
                            dateLabel = completionDate.toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            });
                          }

                          return (
                            <div key={item.id} className="history-item">
                              <div className="history-number">#{history.length - index}</div>
                              <div className="history-user-avatar" style={{ backgroundColor: item.userColor }}>
                                {item.userName.charAt(0).toUpperCase()}
                              </div>
                              <div className="history-details">
                                <div className="history-user-name">{item.userName}</div>
                                <div className="history-date">{dateLabel}</div>
                              </div>
                              <div className="history-points">
                                <Star size={14} /> {item.points}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  <div className="history-stats">
                    <div className="history-stat">
                      <span className="stat-label">Total Completions</span>
                      <span className="stat-value">{getChoreHistory(selectedChoreForHistory.id).length}</span>
                    </div>
                    <div className="history-stat">
                      <span className="stat-label">Total Points</span>
                      <span className="stat-value">
                        <Star size={16} /> {getChoreHistory(selectedChoreForHistory.id).reduce((sum, h) => sum + h.points, 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeView === 'leaderboard' && (
          <div className="view-container">
            <div className="view-header-leaderboard">
              <h2><Trophy size={24} /> Leaderboard</h2>
              <div className="month-selector">
                <button 
                  className="month-nav-btn"
                  onClick={() => changeMonth('prev')}
                  disabled={getAvailableMonths().indexOf(selectedMonth) >= getAvailableMonths().length - 1}
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="month-label">
                  {getMonthLabel(selectedMonth)}
                </span>
                <button 
                  className="month-nav-btn"
                  onClick={() => changeMonth('next')}
                  disabled={getAvailableMonths().indexOf(selectedMonth) <= 0}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>

            {(() => {
              const now = new Date();
              const currentMonth = `${now.getFullYear()}-${now.getMonth()}`;
              const isCurrentMonth = selectedMonth === currentMonth;
              
              return (
                <>
                  {!isCurrentMonth && (
                    <div className="historical-notice">
                      <History size={18} />
                      <span>Viewing historical data - read only</span>
                    </div>
                  )}

                  <div className="leaderboard">
                    {getLeaderboard(selectedMonth).map((user, index) => (
                      <div 
                        key={user.id} 
                        className={`leaderboard-item rank-${index + 1} ${user.isCurrentUser ? 'current-user' : ''}`}
                        style={{ borderLeftColor: user.color }}
                      >
                        <div className="rank-badge" style={{ backgroundColor: user.color }}>
                          {index === 0 && <Crown size={20} />}
                          {index === 1 && <Award size={18} />}
                          {index === 2 && <Star size={16} />}
                          {index > 2 && <span>{index + 1}</span>}
                        </div>
                        
                        <div className="leaderboard-user">
                          <div className="user-avatar-small" style={{ backgroundColor: user.color }}>
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="leaderboard-info">
                            <h3>
                              {user.name}
                              {user.isCurrentUser && <span className="you-badge">You</span>}
                            </h3>
                            <div className="leaderboard-stats">
                              <span><Star size={14} /> {user.stats.points} points</span>
                              <span><CheckCircle size={14} /> {user.stats.count} tasks</span>
                            </div>
                          </div>
                        </div>

                        <div className="progress-bar-container">
                          <div 
                            className="progress-bar" 
                            style={{ 
                              width: `${getLeaderboard(selectedMonth)[0].stats.points > 0 ? (user.stats.points / getLeaderboard(selectedMonth)[0].stats.points) * 100 : 0}%`,
                              backgroundColor: user.color
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </main>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}